import PageHeader from '../../components/PageHeader/PageHeader';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import { IoTrashOutline } from "react-icons/io5";
import { LuPencilLine } from "react-icons/lu";
import { FaFilePdf } from "react-icons/fa6";
import { FaEye } from "react-icons/fa";
import { Modal, Button, Spinner } from 'react-bootstrap';
import EditarNotaFiscal from './EditarNotaFiscal';
import * as XLSX from 'xlsx';
import { urlApiProd } from '../utiles';

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${day}-${month}-${year}`;
};

/** True si el nombre ya trae espesor x ancho x largo (formato maestro), ej. "12 x 1220 x 2440". */
const tieneDimensionesProductoMaestro = (text) =>
    /\d[\d.,]*\s*x\s*\d[\d.,]*\s*x\s*\d[\d.,]*/i.test(text || '');

const CALIDADES_NOMBRE_PRODUCTO = new Set(['BC', 'CD', 'NR', 'RW', 'CLEAR', 'B/BB', 'BB/CC', 'C+/C']);

/** Quita 'mm' como unidad en textos de medida (formato antiguo), alineado al backend. */
const stripMmMedidaAntigua = (s) => {
    if (!s) return s;
    let t = String(s);
    t = t.replace(/(?<=\d)\s*mm\b/gi, '');
    t = t.replace(/\s+mm\b/gi, '');
    return t.trim();
};

/** Misma regla que el backend: "Terciado Pino BC 12 x 1220 x 2440". */
const formatNombreProductoVista = (s) => {
    if (!s || !String(s).trim()) return s;
    return String(s)
        .trim()
        .split(/\s+/)
        .map((raw) => {
            const t = raw;
            if (/^\d+([.,]\d+)?$/.test(t)) return t;
            if (/^x$/i.test(t)) return 'x';
            const u = t.toUpperCase();
            if (CALIDADES_NOMBRE_PRODUCTO.has(u)) return u;
            if (t.includes('/')) {
                if (CALIDADES_NOMBRE_PRODUCTO.has(u)) return u;
                return t
                    .split('/')
                    .map((p) =>
                        p.length > 1 ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : p.toUpperCase()
                    )
                    .join('/');
            }
            return t.length > 1 ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : t.toUpperCase();
        })
        .join(' ');
};

/** Texto producto para Excel: igual que la grilla; solo añade medida en formato antiguo (sin dimensiones x x x). */
const productoDetalleParaExcel = (productoVista, detalle) => {
    const med = (detalle.medida && String(detalle.medida).trim()) || '';
    let base = (productoVista && String(productoVista).trim()) || '';
    if (!base) {
        return formatNombreProductoVista(
            stripMmMedidaAntigua(
                [detalle.producto, detalle.calidad, detalle.medida].filter(Boolean).join(' ').trim()
            )
        );
    }
    if (med && !tieneDimensionesProductoMaestro(base) && !base.includes(med)) {
        base = `${base} ${med}`.trim();
    }
    if (!tieneDimensionesProductoMaestro(base)) {
        base = stripMmMedidaAntigua(base);
    }
    return formatNombreProductoVista(base);
};

const NotasFiscales = () => {
    const [loading, setLoading] = useState(true);
    const [dataNF, setDataNF] = useState([]);
    const [dataProveedores, setDataProveedores] = useState([]);
    const [dataDetalle, setDataDetalle] = useState([]);
    const firstRender = useRef(true);
    const [showAgregar, setShowAgregar] = useState(false);
    const [showEditar, setShowEditar] = useState(false);
    const [selected, setSelected] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState({ title: '', body: '', variant: '' });
    const [selectedDay, setSelectedDay] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedProveedor, setSelectedProveedor] = useState('');
    const [selectedTipoNF, setSelectedTipoNF] = useState('');


    const fetchData = async () => {
        const token = localStorage.getItem('token');
        try {
            const responseNF = await fetch(`${urlApiProd}/notasfiscales/vista_detalles`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (responseNF.status === 401) {
                throw new Error('Unauthorized');
            }
            const dataNF = await responseNF.json();
            setDataNF(dataNF);

            const responseProveedores = await fetch(`${urlApiProd}/proveedores`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (responseProveedores.status === 401) {
                throw new Error('Unauthorized');
            }
            const dataProveedores = await responseProveedores.json();
            setDataProveedores(dataProveedores);

            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setLoading(false);
            handleError("Error recibiendo los datos.");
        }
    };

    const handleError = (message) => {
        setModalContent({
            title: "Error",
            body: message,
            variant: "danger"
        });
        setShowModal(true);
    };

    useEffect(() => {
        if (firstRender.current) {
            fetchData();
            firstRender.current = false;
        }
    }, []);

    const proveedoresMap = useMemo(() => {
        return dataProveedores.reduce((acc, prov) => {
            acc[prov.id_proveedor] = prov.nombre;
            return acc;
        }, {});
    }, [dataProveedores]);

    useEffect(() => {
        const filterByText = dataNF.filter(row =>
            Object.values(row).some(value =>
                value?.toString().toLowerCase().includes(filterText.toLowerCase())
            )
        );

        const filterByDate = filterByText.filter(row => {
            const date = new Date(row.emision);
            const dayMatch = selectedDay ? date.getUTCDate() === parseInt(selectedDay) : true;
            const monthMatch = selectedMonth ? date.getUTCMonth() + 1 === parseInt(selectedMonth) : true;
            const yearMatch = selectedYear ? date.getUTCFullYear() === parseInt(selectedYear) : true;
            return dayMatch && monthMatch && yearMatch;
        });

        const filterByProveedor = filterByDate.filter(row =>
            selectedProveedor ? row.id_proveedor === parseInt(selectedProveedor) : true
        );

        const filterByTipoNF = filterByProveedor.filter(row => {
            if (selectedTipoNF === "entrada") {
                return row.tipo_nf === "0" || row.tipo_nf === "2";
            }
            return selectedTipoNF ? row.tipo_nf === selectedTipoNF : true;
        });

        const filteredWithExtraInfo = filterByTipoNF.map(row => ({
            ...row,
            // Las transformaciones de signos ya se hacen en el backend ahora
        }));

        setFilteredData(filteredWithExtraInfo);
    }, [dataNF, filterText, selectedDay, selectedMonth, selectedYear, selectedProveedor, selectedTipoNF, proveedoresMap]);


    const columns = [
        {
            name: 'Nro NF',
            selector: row => row.nro_nf,
            sortable: true,
        },
        {
            name: 'Proveedor',
            selector: row => row.proveedor_nombre,
            sortable: true,
        },
        {
            name: 'Fecha Emisión',
            selector: row => formatDate(row.emision),
            sortable: true,
        },
        {
            name: 'Producto',
            selector: row => row.producto,
            sortable: true,
            grow: 2,
        },
        {
            name: 'm³',
            selector: row => row.m3,
            sortable: true,
            width: '100px',
            format: row => parseFloat(row.m3).toFixed(3)
        },
        {
            name: 'USD/m³',
            selector: row => row.usd_m3,
            sortable: true,
            width: '100px',
            format: row => `$${parseFloat(row.usd_m3).toFixed(2)}`
        },
        {
            name: 'Total Línea',
            selector: row => row.total_detalle,
            sortable: true,
            width: '120px',
            format: row => `$${parseFloat(row.total_detalle).toFixed(2)}`
        },
        {
            name: 'Total NF',
            selector: row => row.valor_total_nf,
            sortable: true,
            width: '120px',
            format: row => `$${parseFloat(row.valor_total_nf).toFixed(2)}`
        },
        {
            name: 'Booking',
            selector: row => row.booking ? row.booking : 'Sin información',
            sortable: true,
            width: '150px',
        },
        {
            name: 'Acciones',
            cell: row => (
                <div className="d-flex gap-2">
                    <button onClick={() => handleEditar(row)} className="btn btn-sm btn-outline-warning">
                        <LuPencilLine />
                    </button>
                    <button onClick={() => handleEliminar(row.id_nota_fiscal)} className="btn btn-sm btn-outline-danger">
                        <IoTrashOutline />
                    </button>

                </div>
            ),
        },
    ];

    const handleAgregar = () => {
        setShowAgregar(true);
    };

    const handleEditar = (nf) => {
        setSelected(nf);
        setShowEditar(true);
    };

    const handleCloseEditar = () => {
        setSelected(null);
        setShowEditar(false);
    };

    const handleSaveEditar = (edited) => {
        const token = localStorage.getItem('token');
        fetch(`${urlApiProd}/editarnotafiscal/${edited.id_nota_fiscal}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(edited),
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Error al actualizar datos');
                }
                return response.json();
            })
            .then(data => {
                alert('Nota Fiscal actualizada exitosamente');
                handleCloseEditar();
                fetchData(); // Recargar datos sin refrescar la página
            })
            .catch(error => {
                console.error('Error:', error);
                alert('Error al intentar actualizar la Nota Fiscal.');
            });
    };

    const handleExportExcel = async () => {
        const token = localStorage.getItem('token');
        try {
            // IDs únicos (la grilla tiene una fila por detalle; GET con miles de ids rompe el límite de URL en prod)
            const ids = [...new Set(filteredData.map(nf => nf.id_nota_fiscal))];

            const response = await fetch(`${urlApiProd}/notasfiscales/detalles`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ids }),
            });
            const detallesCompletos = await response.json();

            // Mapear los detalles a las columnas del Excel (mismo texto "Producto" que la grilla: vista_detalles)
            const allData = detallesCompletos.map(detalle => {
                const nf = filteredData.find(n => n.id_nota_fiscal === detalle.id_nota_fiscal);
                const rowVista = filteredData.find(
                    r => r.id_detalle === detalle.id_detalle_nf
                );
                const productoGrilla =
                    (detalle.producto_vista && String(detalle.producto_vista).trim()) ||
                    productoDetalleParaExcel(rowVista?.producto, detalle);

                return {
                    'Nro NF': nf?.nro_nf,
                    'Proveedor': nf?.proveedor_nombre,
                    'Fecha Emisión': nf ? formatDate(nf.emision) : '',
                    'Booking': nf?.booking ? nf.booking : 'N/A',
                    'Valor Total NF': detalle.valor_total,
                    'Producto': productoGrilla,
                    'NCM': detalle.ncm,
                    'M3': detalle.m3,
                    'USD/M3': detalle.usd_m3,
                    'Total': detalle.total,
                    'Descuento': detalle.descuento,
                };
            });

            // Generar el archivo Excel
            const ws = XLSX.utils.json_to_sheet(allData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Detalles_Notas_Fiscales");
            XLSX.writeFile(wb, "Detalles_Notas_Fiscales.xlsx");
        } catch (error) {
            console.error('Error al generar el Excel:', error);
        }
    };

    const handleEliminar = (idAEliminar) => {
        const confirmacion = window.confirm('¿Estás seguro de que deseas eliminar esta Nota Fiscal? Esta acción no se puede deshacer.');
        if (confirmacion) {
            const token = localStorage.getItem('token');
            fetch(`${urlApiProd}/eliminarnotafiscal/${idAEliminar}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            })
                .then(response => {
                    if (response.ok) {
                        alert('Nota Fiscal eliminada exitosamente.');
                        fetchData(); // Recargar datos
                    } else {
                        alert('Error al intentar eliminar la Nota Fiscal.');
                    }
                })
                .catch(error => {
                    console.error('Error de red:', error);
                    alert('Ocurrió un error al intentar eliminar la Nota Fiscal.');
                });
        }
    };

    const handleVerDetalles = async (notaFiscalId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${urlApiProd}/notasfiscales/${notaFiscalId}/detalles`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error('Error al obtener los detalles');
            }
            const detalles = await response.json();
            setDataDetalle(detalles);
            setShowModal(true);
            setModalContent({
                title: `Detalles de Nota Fiscal ${notaFiscalId}`,
                body: (
                    <ul>
                        {detalles.map((detalle, index) => (
                            <li key={index}>{detalle.producto} - Total: {detalle.total}</li>
                        ))}
                    </ul>
                ),
                variant: 'primary',
            });
        } catch (error) {
            console.error('Error al obtener detalles:', error);
        }
    };

    return (
        <div className='container-fluid ui-page'>
            <PageHeader
                eyebrow="Registros"
                title="Notas Fiscales"
                descripcion="Registro de notas fiscales de entrada y salida, con su estado y proveedor."
                actions={
                    <>
                        <button type='button' className='ui-btn ui-btn-primary' onClick={() => handleAgregar()}>Agregar Nota Fiscal</button>
                        <button type='button' className='ui-btn ui-btn-secondary' onClick={handleExportExcel}>Extraer Excel</button>
                    </>
                }
            />
            <div className='card'>
                <div className='card-body mb-3'>
                    {loading ? (
                        <>
                            <div className="text-center">
                                <Spinner animation="border" role="status" />
                            </div>
                            <div className='text-center'>
                                <span className="sr-only">Cargando...</span>
                            </div>
                        </>
                    ) : (
                        <>
                            {filteredData.length === 0 && !loading && (
                                <div className="alert alert-warning text-center">
                                    No se encontraron notas fiscales con los criterios seleccionados.
                                </div>
                            )}
                            <div className='input-group mb-3'>
                                <input
                                    type="text"
                                    placeholder='Buscar...'
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    className='form-control'
                                />
                                <select
                                    onChange={(e) => setSelectedProveedor(e.target.value)}
                                    className='form-select'
                                >
                                    <option value="">Todos los Proveedores</option>
                                    {dataProveedores.map(prov => (
                                        <option key={prov.id_proveedor} value={prov.id_proveedor}>
                                            {prov.nombre}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={selectedDay}
                                    onChange={(e) => setSelectedDay(e.target.value)}
                                    className='form-select'
                                >
                                    <option value="">Día</option>
                                    {[...Array(31)].map((_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                            {i + 1}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    className='form-select'
                                >
                                    <option value="">Mes</option>
                                    {[...Array(12)].map((_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                            {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    className='form-select'
                                >
                                    <option value="">Año</option>
                                    {[...Array(5)].map((_, i) => {
                                        const year = new Date().getFullYear() - i;
                                        return <option key={year} value={year}>{year}</option>;
                                    })}
                                </select>
                                <select
                                    value={selectedTipoNF}
                                    onChange={(e) => setSelectedTipoNF(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">Todos los Tipos</option>
                                    <option value="entrada">Entrada</option>
                                    <option value="1">Salida</option>
                                </select>
                            </div>
                            <div className='card'>
                                <div className='card-body'>
                                    <DataTable
                                        columns={columns}
                                        data={filteredData}
                                        noHeader
                                        responsive
                                        pagination
                                        paginationPerPage={30}
                                        highlightOnHover
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Modal para Detalles */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{modalContent.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalContent.body}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
            {showEditar && (
                <EditarNotaFiscal
                    notaFiscal={selected}
                    cerrar={handleCloseEditar}
                    guardar={handleSaveEditar}
                    proveedores={dataProveedores}
                />
            )}
        </div>
    );
};

export default NotasFiscales;
